import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import useFormatter from '../../common/useFormatter';
import * as Icon from '../../components/icons';
import { Container } from '../../components/layout/ContainerFlex';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import TitleSection from '../../components/layout/TitleSection';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import useActualityMetricDescriptors from '../../metrics/actuality/useActualityMetricDescriptors';
import { useActualityAssignmentMetricDescriptors } from '../../metrics/actualityAssignment/useActualityAssignmentMetricDescriptors';
import { actualityMetricSelected } from '../store/actuality.heatmap.state';
import { actualityHeatmapTypeSelected } from '../store/actuality.state';
import {
  MetricGroupId,
  createMetricGroupConfig,
} from '../store/actuality.types';
import { assignmentMetricSelected } from '../store/assignment.heatmap.state';
import { getMetricGroupsConfig } from '../store/metricGroups.config';
import { hiddenMetricGroupsState } from '../store/metricGroups.state';

interface ActualityHeatmapPanelProps {
  heatmapType?: 'events' | 'assignment';
}

const ActualityHeatmapPanel: React.FC<ActualityHeatmapPanelProps> = ({
  heatmapType: externalHeatmapType,
}) => {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const [internalHeatmapType, setInternalHeatmapType] = useRecoilState(
    actualityHeatmapTypeSelected,
  );
  const [hiddenGroups] = useRecoilState(hiddenMetricGroupsState);
  const heatmapType = externalHeatmapType || internalHeatmapType;

  // Update internal state when external prop changes
  React.useEffect(() => {
    if (externalHeatmapType && externalHeatmapType !== internalHeatmapType) {
      setInternalHeatmapType(externalHeatmapType);
    }
  }, [externalHeatmapType, internalHeatmapType, setInternalHeatmapType]);

  const [eventsMetric, setEventsMetric] = useRecoilState(
    actualityMetricSelected,
  );
  const [assignmentMetric, setAssignmentMetric] = useRecoilState(
    assignmentMetricSelected,
  );
  const actualityMetricDescriptors = useActualityMetricDescriptors();
  const assignmentMetricDescriptors = useActualityAssignmentMetricDescriptors();

  const metricDescriptors =
    heatmapType === 'events'
      ? actualityMetricDescriptors
      : assignmentMetricDescriptors;
  const metric = heatmapType === 'events' ? eventsMetric : assignmentMetric;

  function setMetric(v) {
    if (heatmapType === 'events') {
      setEventsMetric(v);
    }
    if (heatmapType === 'assignment') {
      setAssignmentMetric(v);
    }
  }

  // Group metrics by category and map to our configured groups
  const metricGroups = React.useMemo(() => {
    const groups = _.groupBy(metricDescriptors, descriptor => {
      const category = descriptor.category || 'others';
      // Map the category to our configured group IDs
      const groupId =
        (Object.entries(getMetricGroupsConfig(t)).find(
          ([, config]) => config.id.toLowerCase() === category.toLowerCase(),
        )?.[0] as MetricGroupId) || 'others';
      return groupId;
    });

    // Create the final group configs with translations
    return Object.entries(groups)
      .map(([groupId, metrics]) => ({
        ...createMetricGroupConfig(
          t,
          groupId as MetricGroupId,
          true, // defaultVisible parameter
        ),
        id: groupId as MetricGroupId,
        metrics,
        order:
          getMetricGroupsConfig(t)[groupId as MetricGroupId]?.order || 1000,
      }))
      .filter(group => !hiddenGroups.includes(group.id))
      .sort((a, b) => a.order - b.order);
  }, [metricDescriptors, t, hiddenGroups]);

  return (
    <Container col hasOverflowY>
      <ScreenTitle
        title={heatmapType === 'events' ? t`Events` : t`Stock`}
        subtitle={t`Analyse`}
        isSticky
        helpNavTo={
          heatmapType === 'events'
            ? 'actuality/feed-stats'
            : 'actuality/feed-stock'
        }
        icon={heatmapType === 'events' ? Icon.HeatMap9 : Icon.Pallet}
      />

      {/* <ActualityHeatmapTypeSelect 
        value={heatmapType} 
        onChange={(newType) => setInternalHeatmapType(newType)} 
      /> */}

      <StatGroup classNameStatGroupItems={'!space-y-0.5'}>
        {metricGroups.map(group => (
          <TitleSection
            key={`dataset-metric-group-${group.id}`}
            id={`dataset-metric-group-${group.id}`}
            title={group.title}
            inPanelView
            collapsible
            classNameInner="space-y-px"
          >
            {group.metrics.map(d => (
              <Stat
                key={d.title}
                isActionable
                isSelectable
                isSelected={metric === d.type}
                title={d.title}
                onClick={() => setMetric(d.type)}
              />
            ))}
          </TitleSection>
        ))}
      </StatGroup>
    </Container>
  );
};

export default ActualityHeatmapPanel;
